import * as Linking from "expo-linking";
import { useGlobalSearchParams, usePathname, useRouter } from "expo-router";
import { useEffect, useRef } from "react";
import { ActivityIndicator, Platform, StyleSheet, View } from "react-native";

import { useAuth } from "@/lib/auth";
import { captureSentryEvent } from "@/lib/sentry-init";
import { colors } from "@/lib/theme";

/**
 * Custom not-found route.
 *
 * Replaces Expo Router's built-in "Unmatched Route" dev screen (the one with
 * the "Sitemap" link), which would otherwise (a) be reachable in production
 * and (b) bounce the user out to the welcome/login screen when they tapped
 * "Go back" / "Sitemap". Any bad deep link — a malformed push-notification
 * link, a stale URL, a typo in the notification catalog — lands here.
 *
 * We do two things: report the unmatched route to Sentry with enough context
 * to fix the offending link, then redirect the user back into the app instead
 * of stranding them on a dead screen.
 */
export default function NotFoundScreen() {
    const pathname = usePathname();
    const params = useGlobalSearchParams();
    const router = useRouter();
    const { user } = useAuth();
    const reported = useRef(false);

    useEffect(() => {
        // Fire once per mount — usePathname can re-render before we navigate.
        if (reported.current) {
            return;
        }
        reported.current = true;

        const url = Linking.createURL(pathname);
        captureSentryEvent({
            message: `Unmatched route: ${pathname}`,
            level: "error",
            tags: {
                feature: "navigation",
                route_status: "unmatched",
                platform: Platform.OS
            },
            extra: {
                pathname,
                url,
                params,
                rnId: user?.rnId ?? null
            }
        });
        console.warn("[not-found] unmatched route:", pathname, "url:", url);

        // Send the user somewhere real. RootNavigator's auth gate takes over
        // from here (redirects to welcome if unauthenticated).
        router.replace("/(tabs)");
    }, [pathname, params, router, user?.rnId]);

    return (
        <View style={styles.container}>
            <ActivityIndicator size="large" color={colors.teal400} />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.background
    }
});
